/**
 * O que faz: Busca agregados de métricas por domínio com RPC única e cancelamento.
 * Onde usa: src/hooks/useMetricas e seções do dashboard.
 * Conexões: Supabase RPC, Zod via validarRespostaMetricas, contextoMetricas.
 * Tabelas: Leitura via RPCs get_metricas_*_v1 (leads, agenda, vendas, follow-ups, disparos, chat).
 * Data: 05/09/2026.
 */

import { supabase } from '@/integrations/supabase/client';
import type { DominioMetricas, RespostaMetricas } from '../../supabase/functions/_shared/metricas/contratos';
import { validarRespostaMetricas } from '../features/metricas/validarRespostaMetricas';
import type { ContextoMetricas } from '../features/metricas/contextoMetricas';

// Mapa fixo impede que o cliente escolha SQL, coluna ou RPC arbitrária.
const MAPA_RPC_METRICAS: Record<DominioMetricas, string> = {
  comercial: 'get_metricas_comercial_v1',
  followup: 'get_metricas_followup_v1',
  disparos: 'get_metricas_disparos_v1',
  atendimento: 'get_metricas_atendimento_v1',
  operacao: 'get_metricas_operacao_v1',
  agentes: 'get_metricas_agentes_v1',
};

export function erroRepetivel(erro: unknown): boolean {
  // Aborto e erros de permissão/uso nunca disparam nova tentativa automática.
  if (erro instanceof DOMException && erro.name === 'AbortError') return false;
  const status = (erro as { status?: number })?.status;
  if (status === 401 || status === 403 || status === 422 || status === 429) return false;
  const codigo = (erro as { code?: string })?.code;
  if (codigo === '42501' || codigo === 'PGRST301') return false;
  return true;
}

export async function buscarMetricas(dominio: DominioMetricas, contexto: ContextoMetricas, signal?: AbortSignal): Promise<RespostaMetricas> {
  // Clínica e período vêm do contexto autenticado; filtros passam por allowlist do servidor.
  if (!contexto.usuarioId || !contexto.clinicaId) throw new Error('Você não tem acesso às métricas desta clínica.');
  signal?.throwIfAborted();
  const nomeRpc = MAPA_RPC_METRICAS[dominio];
  let consulta = supabase.rpc(nomeRpc, {
    p_clinica_id: contexto.clinicaId,
    p_inicio: contexto.periodo.inicio,
    p_fim: contexto.periodo.fim,
    p_comparar: contexto.comparar,
    p_filtros: contexto.filtros,
  });
  if (signal) consulta = consulta.abortSignal(signal);
  const { data, error } = await consulta;
  signal?.throwIfAborted();
  // Documentar tabelas, operação, campos, filtros e risco antes de cada query:
  // leitura agregada por clínica/período via RPC; sem contatos ou mensagens no payload.
  if (error) {
    if (error.code === '42501') throw new Error('Você não tem acesso às métricas desta clínica.');
    const falha = new Error('Não foi possível carregar estas métricas. Tente novamente.') as Error & { status?: number; code?: string };
    (falha as { status?: number }).status = (error as { status?: number }).status;
    (falha as { code?: string }).code = error.code;
    throw falha;
  }
  try {
    return validarRespostaMetricas(data, dominio) as RespostaMetricas;
  } catch {
    throw new Error('Não foi possível carregar estas métricas. Tente novamente.');
  }
}
